'use client';

import React, { useState } from 'react';
import { Modal, Form, Input, Button } from 'antd';
// 使用 window.message 代替 antd 静态方法，避免 context 警告
import socialLinkService from '../../services/socialLinkService';
import FileUploadList from '../common/FileUploadList';
import { modalStyles } from '../../styles/components/social/PlatformSpecificModal.styles';

const PlatformSpecificModal = ({ platform, open, onClose, onSuccess }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [fileList, setFileList] = useState([]);

  // 处理模态框关闭
  const handleClose = () => {
    // 重置表单和文件列表
    form.resetFields();
    setFileList([]);
    onClose();
  };

  const handleFinish = async (values) => {
    try {
      setLoading(true);

      // 获取上传的文件ID
      const uploadedFileId = fileList.length > 0 ? fileList[0].id : null;

      // 根据平台类型构建不同的请求数据
      let requestData = {};

      switch (platform) {
        case '微信':
          // 根据API文档，微信类型必须有image_attachment_id，url可选
          if (!uploadedFileId) {
            throw new Error('微信类型必须上传二维码图片');
          }
          requestData = {
            type: 'wechat',
            image_attachment_id: uploadedFileId,
            ...(values.link && { url: values.link }),
          };
          break;

        case '微信公众号':
          // 根据API文档，公众号类型必须有image_attachment_id，url可选
          if (!uploadedFileId) {
            throw new Error('公众号类型必须上传二维码图片');
          }
          requestData = {
            type: 'offical_account',
            image_attachment_id: uploadedFileId,
            ...(values.link && { url: values.link }),
          };
          break;

        case '小红书':
          // 根据API文档，小红书类型必须有url
          if (!values.link) {
            throw new Error('小红书类型必须填写链接');
          }
          requestData = {
            type: 'rednote',
            url: values.link,
          };
          break;

        case '抖音':
          // 根据API文档，抖音类型必须有url
          if (!values.link) {
            throw new Error('抖音类型必须填写链接');
          }
          requestData = {
            type: 'tiktok',
            url: values.link,
          };
          break;

        case '微博':
          // 根据API文档，微博类型必须有url
          if (!values.link) {
            throw new Error('微博类型必须填写链接');
          }
          requestData = {
            type: 'weibo',
            url: values.link,
          };
          break;

        case 'BILIBILI':
          // 根据API文档，bilibili类型必须有url
          if (!values.link) {
            throw new Error('Bilibili类型必须填写链接');
          }
          requestData = {
            type: 'bilibili',
            url: values.link,
          };
          break;

        case '即刻':
          // 根据API文档，即刻类型必须有url
          if (!values.link) {
            throw new Error('即刻类型必须填写链接');
          }
          requestData = {
            type: 'jike',
            url: values.link,
          };
          break;

        case 'GITHUB':
          // 根据API文档，github类型必须有display_name，url可选
          if (!values.username) {
            throw new Error('Github类型必须填写用户名');
          }
          requestData = {
            type: 'github',
            display_name: values.username,
            ...(values.username && { url: `https://github.com/${values.username}` }),
          };
          break;

        case 'FIGMA':
          // 根据API文档，figma类型必须有display_name，url可选
          if (!values.username) {
            throw new Error('Figma类型必须填写用户名');
          }
          requestData = {
            type: 'figma',
            display_name: values.username,
            ...(values.username && { url: `https://figma.com/@${values.username}` }),
          };
          break;

        case '知乎':
          // 根据API文档，zhihu类型必须有display_name，url可选
          if (!values.username) {
            throw new Error('知乎类型必须填写用户名');
          }
          requestData = {
            type: 'zhihu',
            display_name: values.username,
            ...(values.username && { url: `https://www.zhihu.com/people/${values.username}` }),
          };
          break;

        case '自定义':
          // 根据API文档，custom类型必须有display_name、url、image_attachment_id
          if (!values.title) {
            throw new Error('自定义类型必须填写标题');
          }
          if (!values.customLink) {
            throw new Error('自定义类型必须填写链接');
          }
          if (!uploadedFileId) {
            throw new Error('自定义类型必须上传图片');
          }
          requestData = {
            type: 'custom',
            display_name: values.title,
            url: values.customLink,
            image_attachment_id: uploadedFileId,
          };
          break;

        default:
          throw new Error('不支持的平台类型');
      }

      console.log('提交社交链接数据:', requestData);

      // 调用API创建社交链接
      const response = await socialLinkService.createSocialLink(requestData);

      window.message?.success('社交链接创建成功！');
      console.log('创建成功:', response);

      // 重置表单和状态
      form.resetFields();
      setFileList([]);
      onClose();

      // 通知父组件刷新数据
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      console.error('创建社交链接失败:', error);
      window.message?.error(error.message || '创建失败，请重试');
    } finally {
      setLoading(false);
    }
  };

  // 文件上传配置
  const uploadOptions = {
    description: `${platform}相关图片`,
    metadata: {
      source: 'social_link',
      platform: platform,
      category: 'image',
    },
  };

  const getModalConfig = () => {
    switch (platform) {
      case '微信':
      case '微信公众号':
        return {
          title: '新增微信联系方式',
          content: (
            <div>
              <p className="text-gray-500 text-sm text-center mb-4">
                请上传您的微信二维码图片
              </p>
              <div className="flex justify-center">
                <FileUploadList
                  fileList={fileList}
                  onChange={setFileList}
                  accept="image/*"
                  maxCount={1}
                  uploadOptions={uploadOptions}
                />
              </div>
            </div>
          ),
        };
      case '小红书':
      case '抖音':
      case '即刻':
      case '微博':
      case 'BILIBILI':
        const platformName = platform || '平台';
        return {
          title: `新增${platformName}`,
          content: (
            <>
              <Form.Item name="link" noStyle>
                <Input
                  size="large"
                  placeholder={`请输入${platformName}主页链接`}
                  className="text-center"
                />
              </Form.Item>
              <p className="text-gray-500 text-xs text-center mt-2">
                在{platformName}-我-右上角分享，获得主页链接
              </p>
            </>
          ),
        };
      case 'GITHUB':
      case 'FIGMA':
      case '知乎':
        const serviceName = platform === 'GITHUB' ? 'Github' : platform;
        return {
          title: `新增${serviceName}`,
          content: (
            <Form.Item name="username" noStyle>
              <Input
                size="large"
                placeholder={`@ 请输入${serviceName}用户名`}
                className="text-center"
              />
            </Form.Item>
          ),
        };
      case '自定义':
        return {
          title: '新增自定义模块',
          content: (
            <div className="flex flex-col gap-6">
              <Form.Item
                label="标题"
                name="title"
                labelCol={{ span: 24 }}
                wrapperCol={{ span: 24 }}
                className="mb-0"
              >
                <Input placeholder="请输入" />
              </Form.Item>
              <Form.Item
                label="自定义封面图"
                labelCol={{ span: 24 }}
                wrapperCol={{ span: 24 }}
                className="mb-0"
              >
                <div className="flex justify-center">
                  <FileUploadList
                    fileList={fileList}
                    onChange={setFileList}
                    accept="image/*"
                    maxCount={1}
                    uploadOptions={uploadOptions}
                  />
                </div>
              </Form.Item>
              <Form.Item
                label="链接"
                name="customLink"
                labelCol={{ span: 24 }}
                wrapperCol={{ span: 24 }}
                className="mb-0"
              >
                <Input placeholder="请输入" />
              </Form.Item>
            </div>
          ),
        };
      default:
        return { title: '', content: null };
    }
  };

  const { title, content } = getModalConfig();

  return (
    <Modal
      title={<span className="text-[--color-primary] font-bold">{title}</span>}
      open={open}
      onCancel={handleClose}
      footer={null}
      centered
      width={400}
      styles={modalStyles}
    >
      <Form form={form} onFinish={handleFinish} layout="vertical">
        <div className="mt-6 mb-6">{content}</div>
        <Form.Item>
          <Button
            type="primary"
            htmlType="submit"
            block
            size="large"
            loading={loading}
          >
            确认发布
          </Button>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default PlatformSpecificModal;
